import {useState} from 'react';

import {move} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {
  BulkPrintDocumentKey,
  GetServiceOrderAvailableDocumentsApiResponse,
} from '@dms/api/workshop';

export interface DocumentSelection {
  isSelected: boolean;
  templateId: string | undefined;
}

export type Selections = Partial<Record<BulkPrintDocumentKey, DocumentSelection>>;

function getDefaultTemplateId(
  availableDocuments: GetServiceOrderAvailableDocumentsApiResponse | undefined,
  documentKey: BulkPrintDocumentKey
) {
  const doc = availableDocuments?.find((d) => d?.key === documentKey);
  const defaultTemplate = doc?.templates?.find((t) => t?.isDefault);
  return defaultTemplate?.id;
}

export interface TemplateOption {
  label: string;
  value: string;
}

function getTemplateOptions(
  availableDocuments: GetServiceOrderAvailableDocumentsApiResponse | undefined,
  documentKey: BulkPrintDocumentKey
): TemplateOption[] {
  const doc = availableDocuments?.find((d) => d?.key === documentKey);
  return (
    doc?.templates?.filter(isNotNil).map((template) => ({
      label: template.title,
      value: template.id,
    })) ?? []
  );
}

// Requirement to have job-card at index 1 if exists
function getVisibleDocuments(
  availableDocuments: GetServiceOrderAvailableDocumentsApiResponse | undefined
): AvailableDocument[] {
  const visibleDocuments = availableDocuments?.filter((doc) => doc.style !== 'hidden') ?? [];

  if (visibleDocuments[1]?.key === 'job-card') {
    return visibleDocuments;
  }

  const jobCardIndex = visibleDocuments?.findIndex((d) => d?.key === 'job-card');
  if (jobCardIndex === -1) {
    return visibleDocuments;
  }

  return move(jobCardIndex, 1, visibleDocuments);
}

type AvailableDocument = GetServiceOrderAvailableDocumentsApiResponse[number];

export function useDocumentSelections(
  availableDocuments: GetServiceOrderAvailableDocumentsApiResponse | undefined,
  generatedKeys: ReadonlySet<string>
) {
  const [selections, setSelections] = useState<Selections>({});

  const visibleDocuments = getVisibleDocuments(availableDocuments);

  const selectableDocuments = visibleDocuments.filter(
    (doc) => doc.style === 'enable' && !generatedKeys.has(doc.key)
  );

  const getSelection = (documentKey: BulkPrintDocumentKey): DocumentSelection => {
    const existing = selections[documentKey];
    if (existing) {
      return existing;
    }
    return {
      isSelected: false,
      templateId: getDefaultTemplateId(availableDocuments, documentKey),
    };
  };

  const handleCheckboxChange = (documentKey: BulkPrintDocumentKey, isSelected: boolean) => {
    setSelections((prev) => ({
      ...prev,
      [documentKey]: {
        isSelected,
        templateId:
          prev[documentKey]?.templateId ?? getDefaultTemplateId(availableDocuments, documentKey),
      },
    }));
  };

  const handleTemplateChange = (
    documentKey: BulkPrintDocumentKey,
    templateId: string | null | number
  ) => {
    setSelections((prev) => ({
      ...prev,
      [documentKey]: {
        isSelected: prev[documentKey]?.isSelected ?? false,
        templateId: typeof templateId === 'string' ? templateId : undefined,
      },
    }));
  };

  const handleSelectAll = () => {
    setSelections((prev) => {
      const next = {...prev};
      for (const doc of selectableDocuments) {
        next[doc.key] = {
          isSelected: true,
          templateId:
            prev[doc.key]?.templateId ?? getDefaultTemplateId(availableDocuments, doc.key),
        };
      }
      return next;
    });
  };

  const handleUnselectAll = () => {
    setSelections((prev) => {
      const next = {...prev};
      for (const doc of selectableDocuments) {
        const current = prev[doc.key] ?? {
          isSelected: false,
          templateId: getDefaultTemplateId(availableDocuments, doc.key),
        };
        next[doc.key] = {...current, isSelected: false};
      }
      return next;
    });
  };

  const isAllSelected =
    selectableDocuments.length > 0 &&
    selectableDocuments.every((doc) => getSelection(doc.key).isSelected);

  const hasSelectedDocuments = selectableDocuments.some((doc) => getSelection(doc.key).isSelected);

  return {
    selections,
    visibleDocuments,
    selectableDocuments,
    isAllSelected,
    hasSelectedDocuments,
    getSelection,
    getTemplateOptions: (documentKey: BulkPrintDocumentKey) =>
      getTemplateOptions(availableDocuments, documentKey),
    handleCheckboxChange,
    handleTemplateChange,
    handleSelectAll,
    handleUnselectAll,
  };
}
